import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// ─── VIDEO DATA ───────────────────────────────────────────────────────────────
// Replace videoId with your actual YouTube video IDs (the part after v= in the URL)
// Thumbnail auto-generates from YouTube's CDN — no need to upload separately.

const videos = [
  {
    id: 1,
    videoId: "o35ixHgUyQ4",
    title: "Secrets of Stunning Hair Transformations: Transplant, Wig, or Magic? | Dr. Bhavesh Gupta Explains",
    duration: "12:45",
    views: "164K views",
    date: "1yr ago",
    featured: true,
  },
  {
    id: 2,
    videoId: "nXXkyKntQ60",
    title: "Truth About FINASTERIDE (propecia) for Hair Loss: Everything You Need to Know | Side Effects |Dosage",
    duration: "8:32",
    views: "31K views",
    date: "1 month ago",
  },
  {
    id: 3,
    videoId: "XkdUI2dAkpc",
    title: "MINOXIDIL: Top 6 Brands In India For Hair Regrowth | Benefits & How it Works & Side Effects",
    duration: "15:20",
    views: "62K views",
    date: "1 month ago",
  },
  {
    id: 4,
    videoId: "gH-MJ52pSn4",
    title: "ITCHING POST TRANSPLANT | REDNESS AFTER TRANSPLANT | PIMPLES AFTER TRANSPLANT",
    duration: "6:14",
    views: "19K views",
    date: "2 months ago",
  },
  {
    id: 5,
    videoId: "UWJwSUWW5Gc",
    title: "Donor Area Recovery After Hair Transplant || Fue Donor Area In Hair Transplant",
    duration: "18:02",
    views: "55K views",
    date: "3 months ago",
  },
];

function getThumbnail(videoId) {
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
}

// ─── PLAY BUTTON ──────────────────────────────────────────────────────────────
function PlayButton({ size = 56 }) {
  return (
    <div
      className="flex items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-110"
      style={{
        width: size, height: size,
        background: "linear-gradient(135deg,#c9943a,#f5d27a,#c9943a)",
        boxShadow: "0 8px 30px rgba(201,168,76,0.4)",
        flexShrink: 0,
      }}
    >
      <svg width={size * 0.36} height={size * 0.36} viewBox="0 0 24 24" fill="#1a0f00">
        <path d="M8 5v14l11-7z" />
      </svg>
    </div>
  );
}

// ─── VIDEO MODAL ──────────────────────────────────────────────────────────────
function VideoModal({ video, onClose }) {
  if (!video) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
      style={{ background: "rgba(5,3,0,0.92)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl"
        style={{ aspectRatio: "16/9" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 flex items-center justify-center rounded-full transition-colors duration-200"
          style={{ width: "40px", height: "40px", border: "1px solid rgba(201,168,76,0.4)", color: "#c9a84c" }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <iframe
          className="w-full h-full rounded-lg"
          src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1`}
          title={video.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function YoutubeSection() {
  const sectionRef = useRef(null);
  const eyebrowRef  = useRef(null);
  const headingRef  = useRef(null);
  const subRef      = useRef(null);
  const featuredRef = useRef(null);
  const cardRefs    = useRef([]);
  const featTitleRef = useRef(null);
  const featMetaRef  = useRef(null);
  const cardTitleRefs = useRef([]);
  const cardMetaRefs  = useRef([]);
  const subscribeBtnRef = useRef(null);
  const [activeVideo, setActiveVideo] = useState(null);

  const featured = videos.find(v => v.featured);
  const rest = videos.filter(v => !v.featured);

  useEffect(() => {
    const ctx = gsap.context(() => {

      // ── BG SCRUB: black → white as section scrolls into view ──
      gsap.fromTo(sectionRef.current,
        { backgroundColor: "#080600" },
        {
          backgroundColor: "#ffffff",
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            end: "top 10%",
            scrub: true,
          },
        }
      );

      // Heading: white → dark
      gsap.fromTo(headingRef.current,
        { color: "#ffffff" },
        {
          color: "#1a0f02",
          ease: "none",
          scrollTrigger: { trigger: sectionRef.current, start: "top 80%", end: "top 10%", scrub: true },
        }
      );

      // Subtitle: white/42% → brown
      gsap.fromTo(subRef.current,
        { color: "rgba(255,255,255,0.42)" },
        {
          color: "#6b5535",
          ease: "none",
          scrollTrigger: { trigger: sectionRef.current, start: "top 80%", end: "top 10%", scrub: true },
        }
      );

      // Featured video title: white → dark
      gsap.fromTo(featTitleRef.current,
        { color: "#ffffff" },
        {
          color: "#1a0f02",
          ease: "none",
          scrollTrigger: { trigger: sectionRef.current, start: "top 80%", end: "top 10%", scrub: true },
        }
      );

      // Featured meta text: white/40% → brown
      gsap.fromTo(featMetaRef.current,
        { color: "rgba(255,255,255,0.4)" },
        {
          color: "#6b5535",
          ease: "none",
          scrollTrigger: { trigger: sectionRef.current, start: "top 80%", end: "top 10%", scrub: true },
        }
      );

      // Featured card border: gold/20% on dark → gold/30% on light
      gsap.fromTo(featuredRef.current?.querySelector(".feat-thumb-wrap"),
        { borderColor: "rgba(201,168,76,0.2)" },
        {
          borderColor: "rgba(160,120,40,0.3)",
          ease: "none",
          scrollTrigger: { trigger: sectionRef.current, start: "top 80%", end: "top 10%", scrub: true },
        }
      );

      // List card titles: white → dark
      cardTitleRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(el,
          { color: "#ffffff" },
          {
            color: "#1a0f02",
            ease: "none",
            scrollTrigger: { trigger: sectionRef.current, start: "top 80%", end: "top 10%", scrub: true },
          }
        );
      });

      // List card meta: white/35% → brown
      cardMetaRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(el,
          { color: "rgba(255,255,255,0.35)" },
          {
            color: "#8a7050",
            ease: "none",
            scrollTrigger: { trigger: sectionRef.current, start: "top 80%", end: "top 10%", scrub: true },
          }
        );
      });

      // List card thumb borders
      cardRefs.current.forEach((el) => {
        if (!el) return;
        const thumb = el.querySelector(".card-thumb-wrap");
        if (!thumb) return;
        gsap.fromTo(thumb,
          { borderColor: "rgba(201,168,76,0.14)" },
          {
            borderColor: "rgba(160,120,40,0.25)",
            ease: "none",
            scrollTrigger: { trigger: sectionRef.current, start: "top 80%", end: "top 10%", scrub: true },
          }
        );
      });

      // Subscribe button: gold-on-dark → gold-on-light
      gsap.fromTo(subscribeBtnRef.current,
        { backgroundColor: "rgba(201,168,76,0.06)", borderColor: "rgba(201,168,76,0.4)" },
        {
          backgroundColor: "rgba(201,168,76,0.1)",
          borderColor: "rgba(160,120,40,0.45)",
          ease: "none",
          scrollTrigger: { trigger: sectionRef.current, start: "top 80%", end: "top 10%", scrub: true },
        }
      );

      // ── ENTRANCE ANIMATIONS ──
      gsap.fromTo(eyebrowRef.current,
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out",
          scrollTrigger: { trigger: eyebrowRef.current, start: "top 88%", toggleActions: "play none none reverse" } }
      );
      gsap.fromTo(headingRef.current,
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power2.out",
          scrollTrigger: { trigger: headingRef.current, start: "top 88%", toggleActions: "play none none reverse" } }
      );
      gsap.fromTo(subRef.current,
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out",
          scrollTrigger: { trigger: subRef.current, start: "top 88%", toggleActions: "play none none reverse" } }
      );
      gsap.fromTo(featuredRef.current,
        { opacity: 0, x: -40 },
        { opacity: 1, x: 0, duration: 0.8, ease: "power2.out",
          scrollTrigger: { trigger: featuredRef.current, start: "top 85%", toggleActions: "play none none reverse" } }
      );
      cardRefs.current.forEach((el, i) => {
        if (!el) return;
        gsap.fromTo(el,
          { opacity: 0, x: 40 },
          {
            opacity: 1, x: 0, duration: 0.6, ease: "power2.out",
            delay: i * 0.08,
            scrollTrigger: { trigger: el, start: "top 92%", toggleActions: "play none none reverse" },
          }
        );
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:wght@300;400;500;600&display=swap"
        rel="stylesheet"
      />

      <section
        ref={sectionRef}
        className="relative w-full overflow-hidden py-24 md:py-28"
        style={{ backgroundColor: "#080600" }} // initial state — GSAP scrubs this to white
      >
        {/* ambient glow */}
        <div
          className="absolute top-0 right-0 w-1/2 h-full pointer-events-none"
          style={{ background: "radial-gradient(ellipse at top right, rgba(201,168,76,0.07) 0%, transparent 60%)" }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">

          {/* ── HEADER ── */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
            <div>
              <p
                ref={eyebrowRef}
                className="opacity-0 mb-3 flex items-center gap-2.5"
                style={{ fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.3em", color: "#c9a84c" }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#c9a84c">
                  <path d="M21.6 7.2s-.21-1.51-.87-2.17c-.83-.87-1.76-.87-2.19-.92C15.4 4 12 4 12 4h-.01s-3.4 0-6.55.11c-.43.05-1.36.05-2.19.92-.66.66-.87 2.17-.87 2.17S2.16 9 2.16 10.8v1.69c0 1.8.18 3.6.18 3.6s.21 1.51.87 2.17c.83.87 1.92.84 2.41.93 1.75.17 7.43.22 7.43.22s3.4-.01 6.55-.12c.43-.05 1.36-.05 2.19-.92.66-.66.87-2.17.87-2.17s.18-1.8.18-3.6v-1.69c0-1.8-.18-3.6-.18-3.6zM9.96 14.5v-5.6l5.4 2.81-5.4 2.79z"/>
                </svg>
                On Our Channel
              </p>
              <h2
                ref={headingRef}
                className="opacity-0 font-black uppercase"
                style={{ fontFamily: "'Cinzel', serif", fontSize: "clamp(1.8rem, 3.2vw, 2.6rem)", letterSpacing: "0.04em", lineHeight: 1.1, color: "#ffffff" }}
              >
                Latest Videos
              </h2>
            </div>

            <p
              ref={subRef}
              className="opacity-0"
              style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 300, fontSize: "14.5px", color: "rgba(255,255,255,0.42)", maxWidth: "360px" }}
            >
              Patient stories, expert advice , live surgeries, case studies from Clinique Internationale straight from our YouTube channel.
            </p>
          </div>

          {/* ── BODY: featured + list ── */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8">

            {/* FEATURED VIDEO */}
            {featured && (
              <div
                ref={featuredRef}
                className="opacity-0 group cursor-pointer"
                onClick={() => setActiveVideo(featured)}
              >
                <div
                  className="feat-thumb-wrap relative rounded-2xl overflow-hidden mb-5"
                  style={{
                    aspectRatio: "16/9",
                    border: "1px solid rgba(201,168,76,0.2)",
                    boxShadow: "0 24px 70px rgba(0,0,0,0.5)",
                  }}
                >
                  <img
                    src={getThumbnail(featured.videoId)}
                    alt={featured.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div
                    className="absolute inset-0"
                    style={{ background: "linear-gradient(180deg, transparent 40%, rgba(5,3,0,0.85) 100%)" }}
                  />
                  {/* play button center */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <PlayButton size={72} />
                  </div>
                  {/* duration badge */}
                  <span
                    className="absolute bottom-4 right-4 px-2.5 py-1 rounded text-white"
                    style={{ fontFamily: "'Raleway', sans-serif", fontSize: "12px", background: "rgba(0,0,0,0.75)" }}
                  >
                    {featured.duration}
                  </span>
                  {/* featured tag */}
                  <span
                    className="absolute top-4 left-4 px-3 py-1 rounded-full uppercase"
                    style={{
                      fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.14em",
                      background: "rgba(201,168,76,0.9)", color: "#1a0f00", fontWeight: 700,
                    }}
                  >
                    Featured
                  </span>
                </div>

                <h3
                  ref={featTitleRef}
                  className="font-bold mb-2 transition-colors duration-200 group-hover:text-[#c9a84c]"
                  style={{ fontFamily: "'Cinzel', serif", fontSize: "19px", letterSpacing: "0.01em", lineHeight: 1.4, color: "#ffffff" }}
                >
                  {featured.title}
                </h3>
                <p
                  ref={featMetaRef}
                  style={{ fontFamily: "'Raleway', sans-serif", fontSize: "13px", color: "rgba(255,255,255,0.4)" }}
                >
                  {featured.views} · {featured.date}
                </p>
              </div>
            )}

            {/* VIDEO LIST */}
            <div className="flex flex-col gap-4">
              {rest.map((v, i) => (
                <div
                  key={v.id}
                  ref={el => cardRefs.current[i] = el}
                  className="opacity-0 group flex gap-4 cursor-pointer p-2 rounded-xl transition-colors duration-200 hover:bg-white/[0.03]"
                  onClick={() => setActiveVideo(v)}
                >
                  {/* thumbnail */}
                  <div
                    className="card-thumb-wrap relative rounded-lg overflow-hidden flex-shrink-0"
                    style={{ width: "160px", aspectRatio: "16/9", border: "1px solid rgba(201,168,76,0.14)" }}
                  >
                    <img
                      src={getThumbnail(v.videoId)}
                      alt={v.title}
                      className="w-full h-full object-cover transition-transform duration-400 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.25)" }}>
                      <PlayButton size={34} />
                    </div>
                    <span
                      className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded text-white"
                      style={{ fontFamily: "'Raleway', sans-serif", fontSize: "10px", background: "rgba(0,0,0,0.8)" }}
                    >
                      {v.duration}
                    </span>
                  </div>

                  {/* meta */}
                  <div className="flex flex-col justify-center min-w-0">
                    <h4
                      ref={el => cardTitleRefs.current[i] = el}
                      className="font-semibold mb-1.5 transition-colors duration-200 group-hover:text-[#c9a84c]"
                      style={{
                        fontFamily: "'Raleway', sans-serif", fontSize: "13.5px", lineHeight: 1.45, color: "#ffffff",
                        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}
                    >
                      {v.title}
                    </h4>
                    <p
                      ref={el => cardMetaRefs.current[i] = el}
                      style={{ fontFamily: "'Raleway', sans-serif", fontSize: "11.5px", color: "rgba(255,255,255,0.35)" }}
                    >
                      {v.views} · {v.date}
                    </p>
                  </div>
                </div>
              ))}

              {/* subscribe CTA */}
              <a
                ref={subscribeBtnRef}
                href="https://youtube.com/@cliniquehairsciencesofficial?si=5eLWUfskOPP_MrIk"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2.5 mt-2 py-3.5 rounded-full transition-all duration-200 hover:brightness-110"
                style={{
                  fontFamily: "'Cinzel', serif", fontSize: "12px", letterSpacing: "0.1em",
                  border: "1px solid rgba(201,168,76,0.4)", color: "#c9a84c",
                  backgroundColor: "rgba(201,168,76,0.06)",
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21.6 7.2s-.21-1.51-.87-2.17c-.83-.87-1.76-.87-2.19-.92C15.4 4 12 4 12 4h-.01s-3.4 0-6.55.11c-.43.05-1.36.05-2.19.92-.66.66-.87 2.17-.87 2.17S2.16 9 2.16 10.8v1.69c0 1.8.18 3.6.18 3.6s.21 1.51.87 2.17c.83.87 1.92.84 2.41.93 1.75.17 7.43.22 7.43.22s3.4-.01 6.55-.12c.43-.05 1.36-.05 2.19-.92.66-.66.87-2.17.87-2.17s.18-1.8.18-3.6v-1.69c0-1.8-.18-3.6-.18-3.6zM9.96 14.5v-5.6l5.4 2.81-5.4 2.79z"/>
                </svg>
                Subscribe on YouTube
              </a>
            </div>

          </div>
        </div>
      </section>

      {/* Modal */}
      {activeVideo && (
        <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
      )}
    </>
  );
}